const MONTHS_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/** "2026-08-26" → "26 de agosto de 2026". Devuelve "—" si no hay fecha válida. */
/**
 * Fechas de CALENDARIO (sin hora: vencimientos, fin de vigencia) llegan del
 * backend como "2026-10-31" o "2026-10-31T00:00:00.000Z". `new Date()` las
 * toma como medianoche UTC, que en Lima (UTC-5) es el día ANTERIOR a las
 * 19:00: la vigencia del 31/10 se mostraba "30 oct" (QA 2026-09-24). Se
 * arman con la fecha tal cual; las que traen hora real se respetan.
 */
export const parseAccountDate = (value: string): Date => {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/.exec(value);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
};

export const formatLongDate = (value?: string | null): string => {
  if (!value) return "—";
  const date = parseAccountDate(value);
  if (isNaN(date.getTime())) return "—";
  return `${date.getDate()} de ${MONTHS_ES[date.getMonth()]} de ${date.getFullYear()}`;
};

/** "2026-08-26" → "26 ago 2026" (formato corto para tarjetas). */
export const formatShortDate = (value?: string | null): string => {
  if (!value) return "—";
  const date = parseAccountDate(value);
  if (isNaN(date.getTime())) return "—";
  const short = MONTHS_ES[date.getMonth()].slice(0, 3);
  return `${date.getDate()} ${short} ${date.getFullYear()}`;
};

/** "Marzo 2024" a partir de una fecha de registro. */
export const formatMonthYear = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  const month = MONTHS_ES[date.getMonth()];
  return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${date.getFullYear()}`;
};

/** Número a moneda peruana: 149 → "S/ 149.00". */
export const formatMoney = (value?: number | null): string => {
  if (value == null || isNaN(value)) return "—";
  return `S/ ${Number(value).toFixed(2)}`;
};

/** Diferencia aproximada en meses desde una fecha hasta hoy, legible. */
export const timeSinceLabel = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  const months =
    (new Date().getFullYear() - date.getFullYear()) * 12 +
    (new Date().getMonth() - date.getMonth());
  if (months <= 0) return "este mes";
  if (months === 1) return "hace 1 mes";
  if (months < 12) return `hace ${months} meses`;
  const years = Math.floor(months / 12);
  return years === 1 ? "hace 1 año" : `hace ${years} años`;
};

// ---------------------------------------------------------------------
// IGV — donde Prikly VENDE, el precio se muestra NETO (sin IGV) con la
// aclaración "+ IGV". El impuesto y el total a cobrar solo aparecen en el
// desglose previo al pago (CheckoutDrawer) y en el comprobante.
//
// El catálogo del backend ya guarda el NETO y varios endpoints publican las
// dos cifras — ahí SIEMPRE se usa la del servidor:
//   /account/plan-services      → periods[].priceWithoutTax  (neto)
//   /account/plan-selected      → priceWithoutTax            (neto)
//   /account/vouchers/packs     → priceWithoutTax            (neto)
//   /account/whatsapp/packs     → priceWithoutIgv            (neto)
//   /account/subscription       → taxBreakdown.base          (neto)
//   /account/pricing/packages   → netPrice                   (neto)
//
// `netOf` es el FALLBACK para los endpoints que hoy solo publican el bruto:
//   /account/subscription/plan-prices   (plans[plan][periodo])
//   /account/subscription/addon-prices  (addons[key])
//   /account/subscription               (addons[].unitPrice, recurringPerCycle)
//   /billing/fe/status                  (addon.price)
//   /whatsapp/status                    (addonPrices[key])
//   /account/plan-services              (catalogAddons[].price)
// Cuando esos expongan el neto, `netOf` se puede retirar del módulo.
// ---------------------------------------------------------------------

/** Tasa de IGV de Perú, alineada con el catálogo de impuestos del backend. */
export const IGV_RATE = 1.18;

/** Total con IGV → base imponible. 2349.14 → 1990.80 */
export const netOf = (total?: number | null): number | null => {
  if (total == null || isNaN(total)) return null;
  return Math.round((Number(total) / IGV_RATE) * 100) / 100;
};

/**
 * Monto YA NETO + la aclaración. 119.90 → "S/ 119.90 + IGV".
 * Es la forma preferida: recibe el neto que publica el servidor.
 */
export const formatNetAmount = (net?: number | null): string => {
  if (net == null || isNaN(net)) return "—";
  return `${formatMoney(net)} + IGV`;
};

/**
 * Igual que `formatNetAmount` pero partiendo del TOTAL con IGV: solo para
 * los endpoints listados arriba que no publican el neto.
 * 2349.14 → "S/ 1990.80 + IGV". Devuelve "—" si no hay monto.
 */
export const formatNetPrice = (total?: number | null): string =>
  formatNetAmount(netOf(total));

/**
 * Precio unitario fino: 0.0716 → "S/ 0.072". Tres decimales porque con dos
 * los paquetes se ven todos igual (0.12 / 0.10 / 0.08 / 0.07) y se pierde el
 * argumento de volumen. No toca IGV: recibe el monto ya neto.
 */
export const formatUnitMoney = (value?: number | null): string => {
  if (value == null || isNaN(value)) return "—";
  return `S/ ${Number(value).toFixed(3)}`;
};
